import { FC, useEffect, useState } from "react";
import cx from 'classnames';
import { NextRouter } from "next/router";
import Dropdown from "components/Dropdown/Dropdown";
import Check from "components/Icons/Check";
import ColorPicker from "components/Icons/ColorPicker";
import { capitalize, titleCase } from "functions/capitalize";
import { slugToString } from "functions/slug";
import { colours } from "./ColorFilter.const";

type ProductTypes =
	'prescription-glasses' |
	'blue-light-glasses' |
	'prescription-sunglasses' |
	'sunglasses' |
	'frames' |
	'arms';

export const colorMap = new Map<string, Array<string>>([
	['blue', ['midnight-blue', 'azure-blue', 'sky-blue', 'denim-blue']],
	['brown', ['gingerbread', 'dark-chocolate', 'sepia-brown', 'oat-milk']],
	['pink', ['power-pink', 'pink-lemonade']],
	['black', ['black']],
	['green', ['forest-green', 'pistachio', 'olive-oil', 'recycled-money', 'mint-julep']],
	['grey', ['slate-grey', 'koala-grey']],
	['white', ['white', 'clear-frost', 'oat-milk']],
	['yellow', ['daffodil-yellow', 'beeswax']],
	['red', ['raspberry-cordial', 'bloody-mary', 'black-cherry']],
	['orange', ['tangerine']],
	['purple', ['royal-purple']]
]);

const colorMapForArms = new Map<string, Array<string>>([
	['blue', ['midnight-blue', 'azure-blue', 'sky-blue', 'denim-blue']],
	['brown', ['gingerbread', 'dark-chocolate', 'sepia-brown', 'oat-milk']],
	['pink', ['power-pink', 'pink-lemonade']],
	['black', ['black']],
	['green', ['forest-green', 'pistachio', 'olive-oil', 'recycled-money', 'mint-julep']],
	['grey', ['slate-grey', 'koala-grey']],
	['white', ['white', 'clear-frost', 'oat-milk']],
	['yellow', ['daffodil-yellow', 'beeswax']],
	['red', ['raspberry-cordial', 'bloody-mary', 'black-cherry']],
	['orange', ['tangerine']],
	['purple', ['royal-purple']]
])

const slugToColorCodeMap = new Map<string, string>([
	['blue', '#278DD3'],
	['brown', '#AD3535'],
	['pink', '#ECB3CF'],
	['black', '#000'],
	['green', '#92AF3D'],
	['grey', '#8A8C8C'],
	['white', '#F8F8F8'],
	['yellow', '#F5EE45'],
	['red', '#EE3535'],
	['orange', '#FFA800'],
	['purple', '#8C64E1']
]);

interface CategoryFrameItem {
	arms_color: {
		name: string;
		slug: string;
	};
	frame_color: {
		name: string;
		slug: string;
	};
	frame_shape: string;
}

interface ColorFilterProps {
	router: NextRouter;
	setSelectedSlugs: React.Dispatch<React.SetStateAction<any>>;
	productType?: ProductTypes;
	categoryFrameList?: CategoryFrameItem[];
}

interface ColorTagProps {
	router: NextRouter;
	slug: string;
	colorFilterMap: Map<string, boolean>;
	productType: ProductTypes;
}

const ColorFilter: FC<ColorFilterProps> = ({
	router,
	setSelectedSlugs,
	productType = 'prescription-glasses',
	categoryFrameList = []
}) => {
	const [colorFilterMap, setColorFilterMap] = useState(new Map<string, boolean>([
		['blue', false],
		['brown', false],
		['pink', false],
		['black', false],
		['green', false],
		['grey', false],
		['white', false],
		['yellow', false],
		['red', false],
		['orange', false],
		['purple', false]
	]));

	const [isColorPickerFilterActive, setIsColorPickerFilterActive] = useState(false);

	useEffect(() => {
		[...colorFilterMap.keys()].forEach((key) => {
			colorFilterMap.set(key, false);
		});
		setColorFilterMap(new Map(colorFilterMap));

		if (router.query.colour == null) {
			[...colorFilterMap.keys()].forEach((key) => {
				colorFilterMap.set(key, false);
			});
			setColorFilterMap(new Map(colorFilterMap));
		} else {
			(typeof router.query.colour) === 'object' ?
				(router.query.colour as string[]).forEach((queryParam) => {
					colorFilterMap.set(queryParam, true)
				})
				: colorFilterMap.set((router.query.colour as string), !colorFilterMap.get((router.query.colour as string)));
			setColorFilterMap(new Map(colorFilterMap));
		}
	}, [router.query])

	useEffect(() => {
		setSelectedSlugs(
			[...colorFilterMap.keys()]
				.filter((key) => {
					return colorFilterMap.get(key)
				})
				.map((color: string) => (
					productType === 'arms' ? colorMapForArms.get(color) : colorMap.get(color)
				))
				.flat()
		)
	}, [colorFilterMap])

	const getBasePath = () => {
		const path = router.pathname.split('/')[1]; // Get the first part of the path (shop or eyewear-selection)
		const { category } = router.query;
		return category ? `/${path}/${category}` : `/${path}/frames`;
	};

	const handleColorTagClick = (slug: string) => {
		let { category, shape, colour } = router.query;
		if (shape === 'rectangle') {
			shape = 'classic';
		}
		let newQuery: any = { ...router.query };
		let pathname = router.pathname;
		const basePath = getBasePath();

		if (Array.isArray(colour)) {
			if (colour.includes(slug)) {
				newQuery.colour = colour.filter(c => c !== slug);
			} else {
				newQuery.colour = [...colour, slug];
			}
		} else if (typeof colour === 'string') {
			if (colour === slug) {
				delete newQuery.colour;
			} else {
				newQuery.colour = [colour, slug];
			}
		} else {
			newQuery.colour = slug;
		}

		if (newQuery.colour && newQuery.colour.length === 0) {
			delete newQuery.colour;
		}

		// Determine the new pathname based on the selected filters
		const hasMultipleColours = Array.isArray(newQuery.colour) && newQuery.colour.length > 1;
		const basePathParts = basePath.split('/');
		const baseRoute = basePathParts[1]; // 'shop' or 'eyewear-selection'

		if (shape) {
			if (newQuery.colour) {
				if (hasMultipleColours) {
					pathname = `/${baseRoute}/[category]/shape/[shape]`;
				} else {
					pathname = `/${baseRoute}/[category]/shape/[shape]/colour/[colour]`;
					newQuery = { ...newQuery, colour: newQuery.colour };
				}
			} else {
				pathname = `/${baseRoute}/[category]/shape/[shape]`;
			}
		} else {
			if (newQuery.colour) {
				if (hasMultipleColours) {
					pathname = `/${baseRoute}/[category]`;
				} else {
					pathname = `/${baseRoute}/[category]/colour/[colour]`;
					newQuery = { ...newQuery, colour: newQuery.colour };
				}
			} else {
				pathname = `/${baseRoute}/[category]`;
			}
		}

		// If there's only one colour, remove it from query and add it to the pathname
		if (newQuery.colour && !hasMultipleColours) {
			pathname = pathname.replace('[colour]', newQuery.colour);
			delete newQuery.colour;
		}

		// Use replace instead of push for immediate effect
		router.replace({
			pathname,
			query: newQuery
		}, undefined, {
			scroll: false // Prevent page jump
		});
	};

	const getAvailableColors = () => {
		let { shape } = router.query;
		if (!shape) {
			return colorFilterMap;
		}

		if (shape === 'rectangle') {
			shape = 'classic';
		}
		
		if (categoryFrameList?.length) {
			// Get all frame colors for the selected shape
			const shapeFrames = categoryFrameList.filter(
				frame => frame.frame_shape.toLowerCase() === (shape as string).toLowerCase()
			);

			// Create a set of available base colors
			const availableColorSlugs = new Set<string>();

			shapeFrames.forEach(frame => {
				// Check each specific color slug against our color mappings
				for (const [baseColor, specificColors] of colorMap.entries()) {
					if (specificColors.includes(frame.frame_color.slug)) {
						availableColorSlugs.add(baseColor);
						break;
					}
				}
			});

			// Filter colorFilterMap to only include available colors
			return new Map([...colorFilterMap].filter(([color]) =>
				availableColorSlugs.has(color)
			));
		}

		return colorFilterMap;
	};

	return (
		<>
			{
				router.query.colour != null ?
					typeof router.query.colour === 'string' ?
						colours.map((colour) => {
							if (router.query.colour === colour) {
								return;
							}
							return (<a
								href={`${getBasePath()}/colour/${colour}`}
								key={colour}
								title={`${capitalize(colour)} ${titleCase(slugToString(router.query?.category ? router.query?.category : productType == 'arms' ? 'spare-parts' : 'frames'))}`}
								className="seo-hidden-link"
							>
								{capitalize(colour) + ' ' + titleCase(slugToString(router.query?.category ? router.query?.category : productType == 'arms' ? 'spare-parts' : 'frames'))}
							</a>);
						}) :
						<>
						</>
					:
					colours.map((colour, index) => (
						(<a
							key={index}
							href={`${getBasePath()}/colour/${colour}`}
							title={`${capitalize(colour)} ${titleCase(slugToString(router.query?.category ? router.query?.category : productType == 'arms' ? 'spare-parts' : 'frames'))}`}
							className="seo-hidden-link"
						>
							{capitalize(colour) + ' ' + titleCase(slugToString(router.query?.category ? router.query?.category : productType == 'arms' ? 'spare-parts' : 'frames'))}
						</a>)
					))
			}
			<div className="flex justify-center md:static fixed z-20 bottom-8 w-full">
				<Dropdown
					onToggle={setIsColorPickerFilterActive}
					className="bg-white w-[160px] h-[48px] shadow-button md:shadow-none"
					wrapperClassName="flex justify-evenly items-center h-full cursor-pointer"
					contentWrapperClassName="!left-auto right-0 shadow-navbarOverlay !rounded-none !mt-0 !px-0 !py-0 min-w-full z-20"
					contentWrapperParentClassName="!overflow-visible !max-h-none w-[505px] border-solid rounded-md border border-[#CACACA]"
					interactionType="MOUSEOVER"
					buttonName={'Apply'}
					title={'Filter by Colour'}
					content={
						<>
							<div className="block md:hidden">
								<div className="grid grid-cols-2 gap-x-9 gap-y-4 py-8 px-4 box-border">
									{
										[...getAvailableColors().keys()].map((slug: string, index: number) => (
											<ColorTag
												key={index}
												router={router}
												colorFilterMap={colorFilterMap}
												slug={slug}
												productType={productType}
												onClick={() => handleColorTagClick(slug)}
											/>
										))
									}
								</div>
							</div>

							<div className="hidden md:grid grid-cols-3 mt-1 ml-4 gap-x-8 gap-y-4 p-4 box-border">
								{
									[...getAvailableColors().keys()].map((slug: string, index: number) => (
										<ColorTag
											key={index}
											router={router}
											colorFilterMap={colorFilterMap}
											slug={slug}
											productType={productType}
											onClick={() => handleColorTagClick(slug)}
										/>
									))
								}
							</div>
						</>
					}
				>
					<div className="flex">
						<span className="text-sm font-medium text-[#616161] mx-2">
							{'Filter by Frame Colour'}
						</span>
					</div>
					<div className="flex items-center border border-[#CACACA] border-solid rounded-md p-2">
						<ColorPicker />
					</div>
				</Dropdown>
			</div>
		</>
	);
}

const ColorTag: FC<ColorTagProps & { onClick: () => void }> = ({ router, slug, colorFilterMap, productType, onClick }) => {
	const getBasePath = () => {
		const path = router.pathname.split('/')[1];
		return `/${path}/${router.query?.category ? router.query?.category : productType == 'arms' ? 'spare-parts' : 'frames'}`;
	};

	return (
		<div className={cx('flex justify-start gap-2 cursor-pointer md:min-h-[auto] min-h-[42px] p-2 md:p-0 box-border',
			{
				'md:border-none border md:bg-white bg-[#F9F9F9] rounded border-[#EAEAEA] border-solid': colorFilterMap.get(slug)
			},
			{
				'md:border-none border border-[transparent] border-solid': !colorFilterMap.get(slug)
			}
		)} onClick={onClick}>
			{
				colorFilterMap.get(slug) == true ?
					(
						<>
							<div className="flex items-center border-[#959595] border-solid lg:border-[3px] rounded-full">
								<div className="md:w-[17px] md:h-[17px] w-[20px] h-[20px] rounded-full lg:border border-solid border-white" style={{ 'background': slugToColorCodeMap.get(slug) }}>
									<span className="flex w-[inherit] h-[inherit] lg:w-auto lg:h-auto lg:mt-1 justify-center items-center">
										{
											slug === 'white' || slug === 'yellow' ?
												(<Check width={8} height={7} fill={'#000'} />) : (<Check width={8} height={7} fill={'#FFF'} />)
										}
									</span>
								</div>
							</div>
							<div className="flex md:text-sm text-base md:font-medium font-normal items-center">
								{titleCase(slug)}
							</div>
						</>
					) :
					(
						<>
							<a
								href={`${getBasePath()}/colour/${slug}`}
								title={`${capitalize(slug)} ${titleCase(slugToString(router.query?.category ? router.query?.category : productType == 'arms' ? 'spare-parts' : 'frames'))}`}
								className="seo-hidden-link"
							>
								{capitalize(slug) + ' ' + titleCase(slugToString(router.query?.category ? router.query?.category : productType == 'arms' ? 'spare-parts' : 'frames'))}
							</a>
							<div className="flex items-center border-[#FFFF] border-solid lg:border-[3px] rounded-full">
								<div className="md:w-[17px] md:h-[17px] w-[20px] h-[20px] rounded-full" style={{ 'background': slugToColorCodeMap.get(slug) }}></div>
							</div>
							<div className="flex md:text-sm text-base font-normal items-center">
								{titleCase(slug)}
							</div>
						</>
					)
			}
		</div>
	)
}

export default ColorFilter;
